import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { useBidsStore } from '@/stores/bids-store'
import type { User } from '@/lib/schemas'
import type { BidMemberInfo } from '@/components/ui/owner-assignment'
import {
  EMPTY_MENTION_GATE,
  canManageBidTeam,
  computeMentionGate,
  type MentionCandidate,
  type MentionGate,
  type MentionedPerson,
} from '@/lib/mention-invites'

// ===================
// Organisation members (cached in the bids store)
// ===================

let orgUsersRequest: Promise<void> | null = null

/**
 * Load the organisation's members into the bids store once, for the @mention
 * picker. Joins a request already in flight, and never touches the store's
 * `error` (a missing list only means the picker offers bid members, as before).
 */
export function ensureOrgUsers(): Promise<void> {
  if (useBidsStore.getState().users.length > 0) return Promise.resolve()
  if (orgUsersRequest) return orgUsersRequest

  const requestedFor = useAuthStore.getState().user?.id ?? null
  const request: Promise<void> = api
    .get<User[]>('/users')
    .then((users) => {
      // A sign-out mid-flight must not hand the next user this org's list.
      if ((useAuthStore.getState().user?.id ?? null) !== requestedFor) return
      useBidsStore.setState({ users })
    })
    .catch(() => {
      /* non-critical */
    })
    .finally(() => {
      if (orgUsersRequest === request) orgUsersRequest = null
    })
  orgUsersRequest = request
  return request
}

// ===================
// Who the picker offers, and who a mention will reach
// ===================

export interface MentionInvites {
  /** The author may bring colleagues onto the bid from the comment box. */
  canAddToBid: boolean
  /** Organisation members not on the bid, never viewers — offered only when `canAddToBid`. */
  nonMemberCandidates: MentionCandidate[]
  /** Sort a body's non-member mentions into addable / unreachable. */
  gateFor: (body: string) => MentionGate
}

/**
 * `allowAdd` is the host's say-so (draft comments only, and only while its
 * composer is open — so a list of closed reply boxes fetches nothing).
 *
 * Until the bid's members have loaded (a bid always has an owner, so an empty
 * list means "not loaded"), nothing is offered and nothing is flagged — every
 * mention would otherwise look like a non-member.
 */
export function useMentionInvites(bidMembers: BidMemberInfo[], allowAdd: boolean): MentionInvites {
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  const orgRole = useAuthStore((s) => s.role)
  const orgUsers = useBidsStore((s) => s.users)

  const membersKnown = bidMembers.length > 0
  const canAddToBid =
    allowAdd && membersKnown && canManageBidTeam(orgRole, bidMembers, currentUserId)

  useEffect(() => {
    if (canAddToBid) void ensureOrgUsers()
  }, [canAddToBid])

  const memberIds = useMemo(() => new Set(bidMembers.map((m) => m.userId)), [bidMembers])

  const nonMemberCandidates = useMemo<MentionCandidate[]>(
    () =>
      canAddToBid
        ? orgUsers
            // View-only colleagues are never offered (Kapil, 2026-09-28); the
            // server refuses them too. A viewer already on the bid is a member
            // and stays mentionable through `bidMembers`.
            .filter((u) => !memberIds.has(u.id) && u.id !== currentUserId && u.role !== 'viewer')
            .map((u) => ({
              userId: u.id,
              role: 'non_member',
              email: u.email,
              fullName: u.fullName ?? null,
              avatarUrl: u.avatarUrl ?? null,
              notOnBid: true,
            }))
        : [],
    [canAddToBid, orgUsers, memberIds, currentUserId],
  )

  const addableIds = useMemo(
    () => new Set(nonMemberCandidates.map((c) => c.userId)),
    [nonMemberCandidates],
  )

  const gateFor = useCallback(
    (body: string): MentionGate =>
      membersKnown
        ? computeMentionGate(body, { memberIds, addableIds, currentUserId })
        : EMPTY_MENTION_GATE,
    [membersKnown, memberIds, addableIds, currentUserId],
  )

  return { canAddToBid, nonMemberCandidates, gateFor }
}

// ===================
// The post-time flow shared by the comment composer and the reply box
// ===================

export interface MentionInviteFlow {
  nonMemberCandidates: MentionCandidate[]
  /** Wire to MentionEditor's `onBodyChange`. */
  onBodyChange: (body: string) => void
  /** The latest body — what "Add and post" posts. */
  body: string
  /** People to confirm adding while the prompt is showing; otherwise null. */
  pendingAdd: MentionedPerson[] | null
  /** Mentioned people nobody is adding, who won't be notified. */
  unreachable: MentionedPerson[]
  /**
   * What posting `body` needs. Returns the ids to add (empty when none) if it
   * may post now, or null when the author must answer the prompt first.
   */
  prepare: (body: string, confirmed: boolean) => string[] | null
  /** Dismiss the prompt and keep editing. Posts nothing. */
  cancel: () => void
  /** After a successful post. */
  reset: () => void
}

export function useMentionInviteFlow(
  bidMembers: BidMemberInfo[],
  allowAdd: boolean,
): MentionInviteFlow {
  const { nonMemberCandidates, gateFor } = useMentionInvites(bidMembers, allowAdd)
  const [body, setBody] = useState('')
  const [confirming, setConfirming] = useState(false)
  const gate = useMemo(() => gateFor(body), [gateFor, body])

  const prepare = (submitted: string, confirmed: boolean): string[] | null => {
    setBody(submitted)
    const next = gateFor(submitted)
    if (next.addable.length > 0 && !confirmed) {
      setConfirming(true)
      return null
    }
    return next.addable.map((p) => p.userId)
  }

  return {
    nonMemberCandidates,
    onBodyChange: setBody,
    body,
    pendingAdd: confirming && gate.addable.length > 0 ? gate.addable : null,
    unreachable: gate.unreachable,
    prepare,
    cancel: () => setConfirming(false),
    reset: () => {
      setBody('')
      setConfirming(false)
    },
  }
}
